import React, { useState } from 'react';
import {
  Box,
  Grid,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import {
  formatClienteDisplay,
  PlanVisitaDireccionField,
  SelectBox,
  visitRowTextFieldProps,
  VisitRowState,
} from './planVisitaFormUi';

interface PlanVisitaItemRowProps {
  row: VisitRowState;
  onPatch: (patch: Partial<VisitRowState>) => void;
  onOpenClientePicker: () => void;
  onOpenDireccionPicker: () => void;
  onRemove?: () => void;
}

const normalizeTimeValue = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  // Native time input expects HH:MM; accept HH:MM:SS by truncating.
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    return trimmed.slice(0, 5);
  }
  return trimmed;
};

const PlanVisitaItemRow: React.FC<PlanVisitaItemRowProps> = ({
  row,
  onPatch,
  onOpenClientePicker,
  onOpenDireccionPicker,
  onRemove,
}) => {
  const [obsAnchor, setObsAnchor] = useState<HTMLElement | null>(null);
  const hasObs = Boolean(row.observaciones?.trim());

  return (
    <Box sx={{ p: 1, border: '1px solid #e0e0e0', borderRadius: 1.5 }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={4}>
          <SelectBox
            label="Cliente"
            display={formatClienteDisplay(row)}
            placeholder="Buscar cliente"
            hideHelper
            onOpen={onOpenClientePicker}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PlanVisitaDireccionField
            row={row}
            onPatch={onPatch}
            onOpenDireccionPicker={onOpenDireccionPicker}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            {...visitRowTextFieldProps}
            label="Horario"
            type="time"
            InputLabelProps={{ shrink: true }}
            value={normalizeTimeValue(row.horarioPreferido)}
            onChange={(e) => onPatch({ horarioPreferido: e.target.value })}
            inputProps={{ step: 300 }}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
            <Tooltip title={hasObs ? 'Editar observación' : 'Agregar observación'}>
              <IconButton
                size="small"
                color={hasObs ? 'primary' : 'default'}
                aria-label="Observación de visita"
                onClick={(e) => setObsAnchor(e.currentTarget)}
              >
                <NotesIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {onRemove ? (
              <IconButton color="error" size="small" aria-label="Quitar visita" onClick={onRemove}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
          <Popover
            open={Boolean(obsAnchor)}
            anchorEl={obsAnchor}
            onClose={() => setObsAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 1.5, width: 280 }}>
              <TextField
                {...visitRowTextFieldProps}
                autoFocus
                label="Obs. visita"
                multiline
                minRows={2}
                value={row.observaciones}
                onChange={(e) => onPatch({ observaciones: e.target.value })}
              />
            </Box>
          </Popover>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlanVisitaItemRow;
